import React, { useState, useEffect, useRef } from "react";
import { useUserContext } from "../../_context/UserContext";
import { useNavigate } from "react-router-dom";
import { mandatoryValueValidator } from "./Validations";
import { AlertComponent } from "../../_modals_and_Notifications/AlertComponent";
import { LoadingSpinnerModal } from "../../_modals_and_Notifications/LoadingSpinnerModal";
import CNALOGO from "../../_resources/Images/logo.png";
import "./Login.css";

const ROLE_OPTIONS = [
  { label: "Admin", value: "admin" },
  { label: "System User", value: "system user" },
];

interface RoleDropdownProps {
  value: string;
  onChange: (v: string) => void;
}

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const RoleDropdown: React.FC<RoleDropdownProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = ROLE_OPTIONS.find(o => o.value === value);

  return (
    <div className={`custom-select-wrapper${open ? " open" : ""}`} ref={ref}>
      <button
        type="button"
        className={`custom-select-trigger${!value ? " placeholder" : ""}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selected ? selected.label : "— Select your role —"}</span>
        <span className={`custom-select-chevron${open ? " open" : ""}`}>
          <ChevronIcon />
        </span>
      </button>

      {open && (
        <ul className="custom-select-menu" role="listbox">
          {ROLE_OPTIONS.map(opt => (
            <li
              key={opt.value}
              role="option"
              aria-selected={value === opt.value}
              className={`custom-select-option${value === opt.value ? " selected" : ""}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              <span className="custom-select-option-label">{opt.label}</span>
              {value === opt.value && (
                <span className="custom-select-option-check"><CheckIcon /></span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Eye icons for the password toggle
const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { loginError, login, isLogged, isUserAdmin, showLoginLoadingScreen, setShowLoginLoadingScreen } =
    useUserContext();
  const navigate = useNavigate();

  const isFormValid = () => {
    return (
      !mandatoryValueValidator(username, true) &&
      !mandatoryValueValidator(password, true) &&
      !mandatoryValueValidator(role, true)
    );
  };

  useEffect(() => {
    if (isLogged) {
      if (isUserAdmin) {
        navigate("/admin");
      } else {
        navigate("/reports");
      }
    }
  }, [isLogged, isUserAdmin, navigate]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isFormValid()) {
      setShowLoginLoadingScreen(true);
      try {
        login(username, password, role).then((userType) => {
          console.log(userType);
          if (userType === "admin") {
            navigate("/admin");
          } else if (userType === "system user") {
            navigate("/reports");
          }
        });
      } catch (error) {
        console.error("Login failed:", error);
        setShowLoginLoadingScreen(false);
        navigate("/");
      }
    }
  };

  return (
    <div className="login-page">
      {/* ── Left: Hero Panel ────────────────────────────────── */}
      <div className="login-hero">
        <img src={CNALOGO} alt="CNA Cleaning Services" className="login-logo" />
        <span className="login-hero-badge">
          <span className="login-hero-badge-dot" />
          CNA VISION
        </span>
        <p className="login-hero-tagline">
          "To be recognised as the most respected and sought after facilities Management organisation in the industry."
        </p>
      </div>

      {/* ── Right: Form Panel ───────────────────────────────── */}
      <div className="login-form-panel">
        <div className="login-card">

          {/* Heading */}
          <div className="login-card-heading">
            <span className="login-card-eyebrow">CNA Cleaning Services</span>
            <h1 className="login-card-title">Welcome back</h1>
            <p className="login-card-subtitle">Sign in to access your dashboard</p>
          </div>

          {/* Error alert */}
          {loginError && (
            <div className="login-alert-wrapper">
              <AlertComponent
                id="loginError"
                variant="danger"
                message={loginError}
                className=""
              />
            </div>
          )}

          {/* Form */}
          <form noValidate onSubmit={handleSubmit}>

            {/* Role selector – custom dropdown */}
            <div className="login-field">
              <label>Login as</label>
              <RoleDropdown value={role} onChange={setRole} />
            </div>

            {/* Username */}
            <div className="login-field">
              <label htmlFor="UserName">User Name</label>
              <input
                id="UserName"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={mandatoryValueValidator(username, true) && username !== "" ? "is-invalid" : ""}
                required
              />
              {mandatoryValueValidator(username, true) && username !== "" && (
                <div className="login-field-error">{mandatoryValueValidator(username, true)}</div>
              )}
            </div>

            {/* Password */}
            <div className="login-field">
              <label htmlFor="Password">Password</label>
              <div className="login-password-wrapper">
                <input
                  id="Password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={mandatoryValueValidator(password, true) && password !== "" ? "is-invalid" : ""}
                  required
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {mandatoryValueValidator(password, true) && password !== "" && (
                <div className="login-field-error">{mandatoryValueValidator(password, true)}</div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="login-btn"
              disabled={!isFormValid()}
            >
              Sign In
            </button>
          </form>
        </div>
      </div>

      <LoadingSpinnerModal ShowModal={showLoginLoadingScreen} />
    </div>
  );
};
